import { EmptyState } from "@/components/molecules/empty-state";
import { ErrorState } from "@/components/molecules/error-state";
import { AFTER_SAVE } from "@/components/molecules/form-footer";
import { PageSkeleton } from "@/components/molecules/page-skeleton";
import { useTranslation } from "@/features/utils/i18n";
import { ClipboardTextIcon } from "@phosphor-icons/react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { PageLayout } from "../layout";
import { FormEditor } from "./components/form-editor";
import {
  useForm,
  useUpdateForm,
} from "@/features/qualifications/hooks/use-qualification-forms";
import type {
  UpdateFormRequest,
  CreateFormRequest,
} from "@/features/qualifications/types";

export function EditQualificationFormPage() {
  const { t } = useTranslation("qualifications");
  const navigate = useNavigate();
  const { company: clientId, formId } = useParams<{
    company: string;
    formId: string;
  }>();

  const { data: form, isLoading, isError, refetch } = useForm(formId!);
  const updateForm = useUpdateForm();

  const listPath = `/${clientId}/settings/qualification-forms`;
  const backTo = { label: t("forms.backToForms"), path: listPath };

  const handleSubmit = async (data: CreateFormRequest | UpdateFormRequest) => {
    try {
      await updateForm.mutateAsync({
        formId: formId!,
        data: data as UpdateFormRequest,
      });
      toast.success(t("forms.messages.updated"));
      navigate(listPath, AFTER_SAVE);
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : t("forms.messages.updateError"),
      );
    }
  };

  if (isLoading)
    return <PageSkeleton width="narrow" blocks={["h-40", "h-96"]} />;

  if (isError || !form) {
    return (
      <PageLayout
        title={t("forms.editPage.title")}
        backTo={backTo}
        width="narrow"
      >
        {isError ? (
          <ErrorState onRetry={refetch} />
        ) : (
          <EmptyState
            icon={ClipboardTextIcon}
            title={t("forms.messages.notFound")}
          />
        )}
      </PageLayout>
    );
  }

  return (
    <PageLayout
      title={t("forms.editPage.editTitle", {
        name: form.name || t("forms.untitled"),
      })}
      description={t("forms.editPage.description")}
      backTo={backTo}
      width="narrow"
    >
      <FormEditor
        initialData={form}
        onSubmit={handleSubmit}
        saving={updateForm.isPending}
      />
    </PageLayout>
  );
}
